import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { DatePicker } from "antd"; // Pagination removed (we use custom)
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
  getCompanyData,
  getUsersFromCompanyName,
} from "../../Service/apiService";
import "../../assets/css/CompanyList.css";
import moment from "moment";
import { FiSearch, FiTrash2 } from "react-icons/fi";

const { RangePicker } = DatePicker;

// ... (your option arrays remain unchanged)
const chapterOption = Array.from({ length: 99 }, (_, i) => ({
  value: (i + 1).toString(),
  label: `Chapter ${i + 1}`,
}));
const createdByOptions = [
  { label: "KS", value: "KS" },
  { label: "JD", value: "JD" },
  { label: "Without Values", value: "without" },
];
const companyStatusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const CompanyList = () => {
  // ... refs
  const selectChapterFilter = useRef();
  const createdByFilter = useRef();
  const companyStatusFilter = useRef();

  // ... states
  const [company, setCompany] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [openRow, setOpenRow] = useState(null);

  const [filterChapter, setFilteredChapter] = useState();
  const [filterText, setFilterText] = useState("");
  const [filterMonth, setFilterMonth] = useState(null);
  const [filterCreatedBy, setFilterCreatedBy] = useState();
  const [filterCompanyStatus, setFilterCompanyStatus] = useState();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const configForHost = window.location.hostname.includes("pharma")
    ? { apiTitle: "Pharmaceuticals" }
    : { apiTitle: "General" };
  const isPharma = configForHost.apiTitle === "Pharmaceuticals";

  const formatExpiryDate = (expiryDate) => {
    if (!expiryDate) return "-";
    const [year, month] = expiryDate.split("-");
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    return `${monthNames[parseInt(month) - 1]}-${year}`;
  };

  const fetchPage = async (page, filters = {}) => {
    const offset = (page - 1) * pageSize;
    setLoading(true);
    try {
      const response = await getCompanyData(offset, filters);
      if (!response?.data) {
        setCompany([]);
        setTotalRecords(0);
        return;
      }
      const pageData = response.data.slice(0, pageSize);
      setCompany(pageData);
      setTotalRecords(response.totalCount || 0);
    } catch (err) {
      console.error("Error:", err);
      setCompany([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    const filters = {
      permissionKey: filterChapter,
      companyName: filterText.trim() || undefined,
      subEndDateRange: filterMonth,
      userCreatedBy: filterCreatedBy,
      companyStatus: filterCompanyStatus,
    };
    fetchPage(1, filters);
  };

  const clearAllFilter = () => {
    selectChapterFilter?.current?.select?.clearValue?.();
    createdByFilter?.current?.select?.clearValue?.();
    companyStatusFilter?.current?.select?.clearValue?.();

    setFilteredChapter(undefined);
    setFilterText("");
    setFilterMonth(null);
    setFilterCreatedBy(undefined);
    setFilterCompanyStatus(undefined);

    setCurrentPage(1);
    fetchPage(1, {});
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const filters = {
      permissionKey: filterChapter,
      companyName: filterText.trim() || undefined,
      subEndDateRange: filterMonth,
      userCreatedBy: filterCreatedBy,
      companyStatus: filterCompanyStatus,
    };
    fetchPage(page, filters);
  };

  const onChapterChange = (selected) => {
    setFilteredChapter(selected ? selected.map((opt) => Number(opt.value)) : undefined);
  };

  const handleMonthChange = (dates) => {
    setFilterMonth(dates);
  };

  const toggleCompanyRow = async (companyName, index) => {
    if (openRow !== index) {
      const response = await getUsersFromCompanyName(companyName);
      setCompanyUsers(response || []);
    }
    setOpenRow(openRow === index ? null : index);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "-" : moment(d).format("DD/MM/YYYY");
  };

  useEffect(() => {
    fetchPage(1, {});
  }, []);

  // ✅ Skeleton Table Component (clean, modern)
  const SkeletonTable = ({ rows = 10 }) => (
    <div className="table-scroll-container">
      <table className="company-table">
        <thead>
          <tr>
            <th>Sr No.</th>
            <th>Company Name</th>
            <th>Country</th>
            <th>Status</th>
            <th>Expires On</th>
            <th>Curated</th>
            <th>1W</th>
            <th>1M</th>
            <th>Overall</th>
            <th>Users</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i}>
              <td><Skeleton width={40} height={20} /></td>
              <td><Skeleton width={120} height={20} /></td>
              <td><Skeleton width={80} height={20} /></td>
              <td><Skeleton width={60} height={20} /></td>
              <td><Skeleton width={90} height={20} /></td>
              <td><Skeleton width={50} height={20} /></td>
              <td><Skeleton width={40} height={20} /></td>
              <td><Skeleton width={40} height={20} /></td>
              <td><Skeleton width={60} height={20} /></td>
              <td><Skeleton circle={true} height={24} width={24} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="company-list-container">
      {/* Filters */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          applyFilters();
        }}
        className="filter-form"
      >
        <div className="filter-row">
          {/* ... your filter groups (unchanged) */}
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search company name"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Select Chapter</label>
            <Select
              isMulti
              ref={selectChapterFilter}
              options={chapterOption}
              onChange={onChapterChange}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          <div className="filter-group">
            <label>Select Month</label>
            <RangePicker
              picker="month"
              onChange={handleMonthChange}
              value={filterMonth}
            />
          </div>

          <div className="filter-group">
            <label>Select Created by</label>
            <Select
              ref={createdByFilter}
              options={createdByOptions}
              onChange={(opt) => setFilterCreatedBy(opt?.value)}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          <div className="filter-group">
            <label>Select Company Status</label>
            <Select
              ref={companyStatusFilter}
              options={companyStatusOptions}
              onChange={(opt) => setFilterCompanyStatus(opt?.value)}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          <div className="filter-actions">
            <button type="submit" className="user-btn-search">
              <FiSearch size={18} />
            </button>
            <button
              type="button"
              className="user-btn-clear"
              onClick={clearAllFilter}
            >
              <FiTrash2 size={18} />
            </button>
          </div>
        </div>
      </form>

      {/* ✅ LOADING: Skeleton Table */}
      {loading ? (
        <SkeletonTable rows={pageSize} />
      ) : (
        /* ✅ DATA: Real Table */
        <div className="table-scroll-container">
          <table className="company-table">
            <thead>
              <tr>
                <th>Sr No.</th>
                <th>Company Name</th>
                <th>Country</th>
                <th>Company Status</th>
                <th>Payment Status</th>
                <th>Paid Date</th>
                <th>Leads Date</th>
                <th>Demo</th>
                <th>Demo Date</th>
                <th>Personal Meeting</th>
                <th>Personal Meeting Date</th>
                <th>Active Users</th>
                <th>Total Users</th>
                <th>Price Per Search</th>
                <th>Add Amount</th>
                <th>Created By</th>
                <th>Status</th>
                <th>Expires On</th>
                <th>Expiry Month</th>
                <th>Curated Count</th>
                <th>1 Week Count</th>
                <th>1 Month Count</th>
                <th>3 Month Count</th>
                <th>6 Month Count</th>
                <th>12 Month Count</th>
                <th>Overall Count</th>
                <th>View Users</th>
              </tr>
            </thead>
            <tbody>
              {company.length > 0 ? (
                company.map((item, index) => (
                  <tr key={item._id || index}>
                    <td>{(currentPage - 1) * pageSize + index + 1}</td>
                    <td>{item._id ? item._id.toUpperCase() : "-"}</td>
                    <td>{item.country || "-"}</td>
                    <td>{item.companyType || "-"}</td>
                    <td>{item.paymentStatus || "-"}</td>
                    <td>{formatDate(item.invoiceDate)}</td>
                    <td>{formatDate(item.leadsDate)}</td>
                    <td>{item.demo || "-"}</td>
                    <td>{formatDate(item.demoDate)}</td>
                    <td>{item.personalMeeting || "-"}</td>
                    <td>{formatDate(item.personalMeetingDate)}</td>
                    <td>{item.totalActiveUsers || 0}</td>
                    <td>{item.totalUsers || 0}</td>
                    <td>
                      {item.pricePerOverall
                        ? `₹${Number(item.pricePerOverall).toFixed(2)}`
                        : "-"}
                    </td>
                    <td>{item.price || "-"}</td>
                    <td>{item.userCreatedBy || "-"}</td>
                    <td>
                      {item.companyStatus
                        ? item.companyStatus.charAt(0).toUpperCase() +
                          item.companyStatus.slice(1)
                        : "-"}
                    </td>
                    <td>{formatDate(item.subEndDate)}</td>
                    <td>{formatExpiryDate(item.expiryDate)}</td>
                    <td>{item.totalCuratedCount || 0}</td>
                    <td>{item.oneWeekTotal || 0}</td>
                    <td>{item.oneMonthTotal || 0}</td>
                    <td>{item.threeMonthTotal || 0}</td>
                    <td>{item.sixMonthTotal || 0}</td>
                    <td>{item.twelveMonthTotal || 0}</td>
                    <td>{item.overall || 0}</td>
                    <td>
                      <span
                        className="view-users-icon"
                        onClick={() => toggleCompanyRow(item._id, index)}
                      >
                        👁️
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="28" className="no-data">
                    No companies found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination — remains visible always */}
      {totalRecords > 0 && !loading && (
        <div className="custom-pagination">
          <button
            className={`pagination-btn ${currentPage === 1 ? "disabled" : ""}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>

          <span className="page-info">
            Page {currentPage} of {Math.ceil(totalRecords / pageSize)}
          </span>

          <button
            className={`pagination-btn ${
              currentPage === Math.ceil(totalRecords / pageSize) ? "disabled" : ""
            }`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === Math.ceil(totalRecords / pageSize)}
          >
            Next →
          </button>
        </div>
      )}

      {/* Modal (unchanged) */}
      {openRow !== null && (
        <div className="modal-overlay" onClick={() => setOpenRow(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Users of {company[openRow]?._id || "Company"}</h3>
              <button className="modal-close" onClick={() => setOpenRow(null)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Sr No.</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>1W</th>
                    <th>1M</th>
                    <th>3M</th>
                    <th>6M</th>
                    <th>12M</th>
                  </tr>
                </thead>
                <tbody>
                  {companyUsers.length > 0 ? (
                    companyUsers.map((user, idx) => (
                      <tr key={user._id || idx}>
                        <td>{idx + 1}</td>
                        <td>{user.name || "-"}</td>
                        <td>{user.email || "-"}</td>
                        <td>{user.searchCounts?.oneWeekCount || 0}</td>
                        <td>{user.searchCounts?.oneMonthCount || 0}</td>
                        <td>{user.searchCounts?.threeMonthCount || 0}</td>
                        <td>{user.searchCounts?.sixMonthCount || 0}</td>
                        <td>{user.searchCounts?.twelveMonthCount || 0}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="no-data">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyList;